import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, CheckCircle, AlertCircle, Loader2, Stethoscope } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from 'react-i18next';

export default function EmailConfirmationPage() {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('doctor-platform');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // The confirmation link can arrive in three shapes depending on the
        // GoTrue/email-template flow: a PKCE `?code=`, an implicit
        // `#access_token=…` hash, or an OTP `?token_hash=&type=`. The client also
        // has detectSessionInUrl enabled, so it may already have consumed and
        // cleaned the URL before this runs. Check for an existing session first,
        // then fall back to whichever token style the link actually carried.
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const tokenHash = url.searchParams.get('token_hash');
        const tokenType = url.searchParams.get('type');
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        let session = (await supabase.auth.getSession()).data.session;

        if (!session) {
          try {
            if (code) {
              await supabase.auth.exchangeCodeForSession(code);
            } else if (accessToken) {
              await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || '',
              });
            } else if (tokenHash && tokenType) {
              await supabase.auth.verifyOtp({
                type: tokenType as 'signup' | 'email_change' | 'invite' | 'recovery' | 'magiclink',
                token_hash: tokenHash,
              });
            }
          } catch {
            // detectSessionInUrl may have consumed the token in a race; defer the
            // verdict to the session re-check below rather than failing outright.
          }
          session = (await supabase.auth.getSession()).data.session;
          if (!session) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            session = (await supabase.auth.getSession()).data.session;
          }
        }

        if (!session) {
          throw new Error(t('auth.emailConfirmation.errors.invalidToken'));
        }

        // Get user details
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (user) {
          setUserEmail(user.email || '');
          setIsVerified(true);
          
          toast({
            title: t('auth.emailConfirmation.toast.verifiedTitle'),
            description: t('auth.emailConfirmation.toast.verifiedDescription'),
          });

          // Decide where to land based on how far the doctor has progressed
          // through onboarding. A freshly-confirmed signup has no doctor_profiles
          // row yet and must complete the profile-setup step first.
          const { data: profile } = await supabase
            .from('doctor_profiles')
            .select('prescribing_enabled, verified_at')
            .eq('auth_user_id', user.id)
            .maybeSingle();

          const isApproved = Boolean(profile?.verified_at || profile?.prescribing_enabled);

          setTimeout(() => {
            if (!profile) {
              // No profile yet → complete onboarding.
              navigate('/auth/setup-profile', { replace: true });
            } else if (isApproved) {
              navigate('/dashboard', { replace: true });
            } else {
              // Profile submitted, awaiting admin approval.
              navigate('/pending-approval', { replace: true });
            }
          }, 2000);
        }
      } catch (error: any) {
        console.error('Email verification failed:', error);
        setError(error.message || t('auth.emailConfirmation.errors.verificationFailed'));
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [navigate, t, toast]);

  const handleResendEmail = async () => {
    if (!userEmail) {
      setError(t('auth.emailConfirmation.errors.loginRequired'));
      return;
    }

    setIsVerifying(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
      });

      if (error) throw error;

      toast({
        title: t('auth.emailConfirmation.toast.resentTitle'),
        description: t('auth.emailConfirmation.toast.resentDescription'),
      });
    } catch (error: any) {
      console.error('Failed to resend email:', error);
      setError(t('auth.emailConfirmation.errors.resendFailed'));
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-center text-muted-foreground">
                {t('auth.emailConfirmation.verifying')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {isVerified ? (
              <div className="h-16 w-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            ) : error ? (
              <div className="h-16 w-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            ) : (
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {isVerified
              ? t('auth.emailConfirmation.titles.verified')
              : error
                ? t('auth.emailConfirmation.titles.failed')
                : t('auth.emailConfirmation.titles.default')}
          </CardTitle>
          <CardDescription>
            {isVerified 
              ? t('auth.emailConfirmation.descriptions.verified')
              : error 
                ? t('auth.emailConfirmation.descriptions.failed')
                : t('auth.emailConfirmation.descriptions.default')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isVerified && (
            <div className="text-center space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <Stethoscope className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('auth.emailConfirmation.welcome')}
                </p>
              </div>
              {userEmail && (
                <p className="text-sm text-muted-foreground">
                  <strong>{t('auth.emailConfirmation.verifiedEmail', { email: userEmail })}</strong>
                </p>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!isVerified && (
            <div className="space-y-4">
              <Button
                onClick={handleResendEmail}
                className="w-full"
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.emailConfirmation.sending')}
                  </>
                ) : (
                  t('auth.emailConfirmation.resend')
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/auth/login')}
                className="w-full"
              >
                {t('auth.emailConfirmation.backToLogin')}
              </Button>
            </div>
          )}

          {isVerified && (
            <div className="flex justify-center space-x-4 pt-4">
              <Button
                onClick={() => navigate('/dashboard')}
                className="min-w-[120px]"
              >
                {t('auth.emailConfirmation.goToDashboard')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
